import { formatText } from '@arnold/common';
import { getLocale } from '@arnold/core';
import { Document, Font, Image, Page, Path, pdf, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import { format } from 'date-fns';
import {
  AnonymityLevel,
  QuestionType,
  ReportSequenceRespondentDetailQueryQuery,
  SurveyStatus,
} from '../../generated/hooks';
import {
  getAlertsData,
  getPrintableReportMonth,
  getQuestionDefinitionByIndex,
  getTeamNameByRespondentId,
  getTeamsLabel,
  getTopicGroupTranslation,
} from '../reportHelper';
import { IMessage } from '../../reports/components/RespondentsAnswers';
import { AnswerType } from './question';

Font.register({
  family: 'Quicksand',
  fontWeight: 'normal',
  src: 'https://fonts.gstatic.com/s/quicksand/v6/sKd0EMYPAh5PYCRKSryvW6CWcynf_cDxXwCLxiixG1c.ttf',
});
Font.register({
  family: 'Quicksand',
  fontWeight: 'bold',
  src: 'https://fonts.gstatic.com/s/quicksand/v6/32nyIRHyCu6iqEka_hbKsi3USBnSvpkopQaUR-2r7iU.ttf',
});
Font.register({
  family: 'Roboto',
  fontWeight: 'normal',
  src: 'https://fonts.gstatic.com/s/roboto/v15/W5F8_SL0XFawnjxHGsZjJA.ttf',
});
Font.register({
  family: 'Roboto',
  fontWeight: 'semibold',
  src: 'https://fonts.gstatic.com/s/roboto/v15/Uxzkqj-MIMWle-XP2pDNAA.ttf',
});
Font.register({
  family: 'Roboto',
  fontWeight: 'light',
  src: 'https://fonts.gstatic.com/s/roboto/v15/dtpHsbgPEm2lVWciJZ0P-A.ttf',
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
  },
  title: {
    fontFamily: 'Quicksand',
    fontWeight: 'bold',
    fontSize: 32,
    color: '#3f3f3f',
  },
  meta: {
    marginTop: 4,
    fontFamily: 'Roboto',
    fontSize: 12,
    color: '#a1a1a1',
  },
  conversationSubline: {
    marginTop: 5,
    fontFamily: 'Roboto',
    fontSize: 12,
    color: '#3f3f3f',
  },
  heading: {
    marginTop: 12,
    fontFamily: 'Roboto',
    fontWeight: 'thin',
    fontSize: 24,
  },
  messageQuestion: {
    marginTop: 12,
    fontFamily: 'Roboto',
    fontSize: 10,
    padding: 3,
    paddingLeft: 12,
    color: '#a1a1a1',
  },
  scaleStatement: {
    marginLeft: 18,
  },
  messageAnswer: {
    marginTop: 5,
    marginLeft: 5,
    fontFamily: 'Roboto',
    fontSize: 12,
    color: '#3f3f3f',
  },
  conversationTitle: {
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: 'semibold',
    color: '#3f3f3f',
  },
  topic: {
    marginTop: 24,
  },
  section: {
    marginTop: 12,
    marginBottom: 16,
  },
  answerContainer: {
    backgroundColor: '#f9f9f9',
    padding: 3,
    paddingLeft: 12,
    borderRadius: 5,
    borderBottomLeftRadius: 0,
    minHeight: 30,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 35,
  },
  footerText: {
    fontFamily: 'Roboto',
    fontSize: 10,
    color: '#3f3f3f',
  },
  imageSmall: {
    height: 24,
    width: 96,
  },
  imageBig: {
    position: 'relative',
    left: -8,
    height: 36,
    width: 144,
  },
  icon: {
    position: 'relative',
    top: 2,
  },
});

export const exportPdf = async (
  respondent: ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail']['respondents'][0],
  topicGroupTranslation: string,
  report: ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail'],
  t: (i18n: string) => string,
  language: string,
  selectedRespondentId: string,
  team?: ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail']['teams'][0],
  step?: NonNullable<
    ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail']['surveyGroup']
  >['steps'][0],
  logEvent?: () => void,
) => {
  const teamName = getTeamNameByRespondentId(report, selectedRespondentId, t);

  const getStepData = (
    step: NonNullable<
      ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail']['surveyGroup']
    >['steps'][0],
  ) => {
    const chat = getAlertsData(report, t, false, true).filter(
      (chat) =>
        chat.messages.length &&
        chat.respondentId === selectedRespondentId &&
        chat.topicGroupId === step.processStep.stepTopicGroup.id,
    )[0];

    const groupedMessages = Array.from(
      (chat?.messages ?? [])
        .reduce((groups, message) => {
          const messages = groups.get(message.questionDefinitionIndex) ?? [];
          messages.push(message);
          groups.set(message.questionDefinitionIndex, messages);
          return groups;
        }, new Map<string, IMessage[]>())
        .entries(),
    );

    const survey = step.surveys.find((r) => (chat ? r.id === chat.surveyId : r.seqDate === respondent.addedOn));
    const surveyIsNotFinished =
      survey && [SurveyStatus.Ongoing, SurveyStatus.Prepared, SurveyStatus.Notstarted].includes(survey.status);

    const endsAt = chat ? chat.date : survey ? new Date(survey.endsAt) : null;
    return (
      <View style={styles.topic}>
        <Text style={styles.conversationTitle}>
          {getTopicGroupTranslation(step.processStep.stepTopicGroup, report.metadata.language, '').value}
        </Text>
        <Text style={styles.conversationSubline}>
          {survey?.sendAt && format(new Date(survey?.sendAt), 'PPPp', { locale: getLocale(language) })}
          {endsAt &&
            ` ${survey?.sendAt ? ' - ' : ''} ${format(endsAt, 'PPPp', {
              locale: getLocale(language),
            })}`}
          {`     ${teamName.teamLabel}: ${teamName.teamName}`}
        </Text>
        {groupedMessages.map(([questionDefinitionIndex, messages]) => {
          const qd = getQuestionDefinitionByIndex(report, questionDefinitionIndex);
          const question = qd?.text || messages[0]?.question || '';
          return (
            <View key={`question-${questionDefinitionIndex}`}>
              <Text style={styles.messageQuestion}>{formatText(question)}</Text>
              {messages.map((message, messageIndex) => (
                <View
                  key={`message-${message.id || messageIndex}`}
                  wrap={
                    qd?.type === QuestionType.Freetext &&
                    message.answers.map((answer) => answer.text).join('').length > 100
                  }
                >
                  {qd?.type === QuestionType.Scale && (
                    <Text style={[styles.messageQuestion, styles.scaleStatement]}>{formatText(message.question)}</Text>
                  )}
                  {qd && qd.type === QuestionType.Select ? (
                    <View style={styles.answerContainer}>
                      {message.answers.map((ans, answerIndex) => (
                        <View key={`answer-${messageIndex}-${answerIndex}`} style={styles.row}>
                          {ans.type === AnswerType.ALERT && (
                            <Svg width="14" height="14" viewBox="0 0 18 16" fill="none" style={styles.icon}>
                              <Path
                                fillRule="evenodd"
                                clip-rule="evenodd"
                                d="M9.24988 16C13.6682 16 17.2499 12.4183 17.2499 8C17.2499 3.58172 13.6682 0 9.24988 0C4.83161 0 1.24988 3.58172 1.24988 8C1.24988 9.43769 1.62913 10.7868 2.29303 11.9528L0.847168 15.6788L4.46016 14.4083C5.79545 15.4079 7.45353 16 9.24988 16ZM8.33008 6.91687C8.33008 7.42104 8.74258 7.83354 9.24674 7.83354C9.75091 7.83354 10.1634 7.42104 10.1634 6.91687V5.08354C10.1634 4.57937 9.75091 4.16687 9.24674 4.16687C8.74258 4.16687 8.33008 4.57937 8.33008 5.08354V6.91687ZM8.33008 11.5002H10.1634V9.66687H8.33008V11.5002Z"
                                fill="#E15E5E"
                              />
                            </Svg>
                          )}
                          {ans.type === AnswerType.WARNING && (
                            <Svg width="14" height="14" viewBox="0 0 18 16" fill="none" style={styles.icon}>
                              <Path
                                fillRule="evenodd"
                                clip-rule="evenodd"
                                d="M9.24988 16C13.6682 16 17.2499 12.4183 17.2499 8C17.2499 3.58172 13.6682 0 9.24988 0C4.83161 0 1.24988 3.58172 1.24988 8C1.24988 9.43769 1.62913 10.7868 2.29303 11.9528L0.847168 15.6788L4.46016 14.4083C5.79545 15.4079 7.45353 16 9.24988 16ZM8.33008 6.91687C8.33008 7.42104 8.74258 7.83354 9.24674 7.83354C9.75091 7.83354 10.1634 7.42104 10.1634 6.91687V5.08354C10.1634 4.57937 9.75091 4.16687 9.24674 4.16687C8.74258 4.16687 8.33008 4.57937 8.33008 5.08354V6.91687ZM8.33008 11.5002H10.1634V9.66687H8.33008V11.5002Z"
                                fill="#F9C475"
                              />
                            </Svg>
                          )}

                          <Text style={styles.messageAnswer}>{ans.text.trim()}</Text>
                        </View>
                      ))}
                    </View>
                  ) : (
                    <View
                      style={
                        qd?.type === QuestionType.Scale
                          ? [styles.answerContainer, styles.scaleStatement]
                          : styles.answerContainer
                      }
                    >
                      <Text style={styles.messageAnswer} wrap={true}>
                        {message.answers.map((answer) => answer.text).join('')}
                      </Text>
                    </View>
                  )}
                </View>
              ))}
            </View>
          );
        })}
        {groupedMessages.length === 0 && (
          <Text style={styles.messageAnswer}>
            {t(
              surveyIsNotFinished
                ? 'withoutResponseForNow'
                : step.processStep.stepTopicGroup.allowedAnonymities?.includes(AnonymityLevel.Organization)
                  ? 'anonymousResponse'
                  : 'noResponse',
            )}
          </Text>
        )}
      </View>
    );
  };

  const doc = (
    <Document>
      <Page size="A4" style={styles.page}>
        <Image style={styles.imageBig} src="/imagesForPresentations/arnoldLogoBlack.png" />
        <Text style={styles.title}>
          {respondent.firstname} {respondent.surname}
        </Text>
        <View style={styles.section}>
          <Text style={styles.meta}>{topicGroupTranslation}</Text>
          <Text style={styles.meta}>{`${t('organization')}: ${report.metadata.organization.name}`}</Text>
          <Text style={styles.meta}>{getTeamsLabel(team ? [team] : [], t)}</Text>
          <Text style={styles.meta}>
            {t('period')}: {getPrintableReportMonth(report, report.metadata.language && report.metadata.language.code)}
          </Text>
          <Text style={styles.meta}>
            {t('ExcelExport:exported')} {format(new Date(), 'PPPp', { locale: getLocale(language) })}
          </Text>
        </View>
        <Text style={styles.heading}>{t('chats')}</Text>
        {step ? getStepData(step) : report.surveyGroup?.steps.map((step) => getStepData(step))}
        <View fixed style={styles.pageNumber}>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
            fixed
          />
          <Image style={styles.imageSmall} src="/imagesForPresentations/arnoldLogoBlack.png" />
        </View>
      </Page>
    </Document>
  );

  // @ts-ignore
  const asPdf = pdf([]); // {} is important, throws without an argument
  asPdf.updateContainer(doc);
  const blob = await asPdf.toBlob();
  const blobUrl = window.URL.createObjectURL(blob);
  // window.open(blobUrl, '_blank');

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = `${respondent.firstname} ${respondent.surname} - ${topicGroupTranslation}.pdf`;
  link.target = '_blank';
  link.click();
  // It's important to revoke the object URL to avoid memory leaks
  URL.revokeObjectURL(blobUrl);
  logEvent && logEvent();
};
